<script setup>
/* global axios */
// Aba Agenda — toolbar (navegação, dia/semana/mês, filtro), calendário e painel de detalhe.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import AppointmentModal from './AppointmentModal.vue';
import {
  STATUS,
  ICON,
  fmtMoney,
  fmtTime,
  sameDay,
  startOfWeek,
} from './constants';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
});
const { t } = useI18n();
const base = `/api/v1/accounts/${props.accountId}/kanban`;

const practitioners = ref([]);
const services = ref([]);
const healthPlans = ref([]);
const appointments = ref([]);

const viewMode = ref('week');
const cursor = ref(new Date());
const fPractitioner = ref('');
const fService = ref('');
const HOUR_H = 56;
const DAY_START = 7;
const DAY_END = 21;
const hours = Array.from(
  { length: DAY_END - DAY_START },
  (_, i) => DAY_START + i
);

const weekDays = computed(() => {
  const s = startOfWeek(cursor.value);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(s);
    d.setDate(s.getDate() + i);
    return d;
  });
});
const visibleDays = computed(() =>
  viewMode.value === 'day' ? [new Date(cursor.value)] : weekDays.value
);
const apptsOn = d =>
  appointments.value.filter(
    a =>
      a.appointment_at &&
      sameDay(new Date(a.appointment_at), d) &&
      (!fPractitioner.value ||
        a.practitioner_id === Number(fPractitioner.value)) &&
      (!fService.value || a.service_id === Number(fService.value))
  );
const apptStyle = a => {
  const s = new Date(a.appointment_at);
  const e = new Date(a.ends_at || s);
  return {
    top: `${(s.getHours() - DAY_START + s.getMinutes() / 60) * HOUR_H}px`,
    height: `${Math.max(22, ((e - s) / 3600000) * HOUR_H)}px`,
  };
};
const rangeLabel = computed(() => {
  if (viewMode.value === 'day')
    return cursor.value.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  if (viewMode.value === 'month')
    return cursor.value.toLocaleDateString('pt-BR', {
      month: 'long',
      year: 'numeric',
    });
  const w = weekDays.value;
  return `${w[0].toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })} – ${w[6].toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })}`;
});
const monthGrid = computed(() => {
  const y = cursor.value.getFullYear();
  const m = cursor.value.getMonth();
  const first = new Date(y, m, 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return { date: d, inMonth: d.getMonth() === m, appts: apptsOn(d) };
  });
});
const rangeBounds = () => {
  let from;
  let to;
  if (viewMode.value === 'month') {
    const y = cursor.value.getFullYear();
    const m = cursor.value.getMonth();
    from = new Date(y, m - 1, 20);
    to = new Date(y, m + 1, 10);
  } else {
    from = startOfWeek(cursor.value);
    to = new Date(from);
    to.setDate(from.getDate() + 7);
  }
  return { from: from.toISOString(), to: to.toISOString() };
};

const loadAppointments = async () => {
  const { from, to } = rangeBounds();
  appointments.value =
    (await axios.get(`${base}/appointments`, { params: { from, to } })).data
      .data || [];
};
const shift = n => {
  const d = new Date(cursor.value);
  if (viewMode.value === 'day') d.setDate(d.getDate() + n);
  else if (viewMode.value === 'week') d.setDate(d.getDate() + n * 7);
  else d.setMonth(d.getMonth() + n);
  cursor.value = d;
  loadAppointments();
};
const setView = v => {
  viewMode.value = v;
  loadAppointments();
};
const today = () => {
  cursor.value = new Date();
  loadAppointments();
};

// modal
const showModal = ref(false);
const editing = ref(null);
const prefill = ref({});
const openNew = (date = null, hour = null) => {
  editing.value = null;
  const d = date ? new Date(date) : new Date();
  prefill.value = {
    date: d.toISOString().slice(0, 10),
    time: hour != null ? `${String(hour).padStart(2, '0')}:00` : '',
  };
  showModal.value = true;
};
const onSaved = () => {
  showModal.value = false;
  loadAppointments();
};

// detalhe
const detail = ref(null);
const setStatus = async (a, st) => {
  await axios.patch(`${base}/appointments/${a.id}`, { status: st });
  await loadAppointments();
  detail.value = appointments.value.find(x => x.id === a.id) || null;
};
const cancelAppt = async a => {
  const r = window.prompt(t('ATENDIMENTOS.agenda.cancelReasonPrompt')) ?? '';
  await axios.patch(`${base}/appointments/${a.id}/cancel`, {
    cancel_reason: r,
  });
  detail.value = null;
  await loadAppointments();
};
const editAppt = a => {
  editing.value = a;
  detail.value = null;
  showModal.value = true;
};

onMounted(async () => {
  practitioners.value =
    (await axios.get(`${base}/practitioners`)).data.data || [];
  services.value =
    (await axios.get(`${base}/appointment_services`)).data.data || [];
  healthPlans.value = (await axios.get(`${base}/health_plans`)).data.data || [];
  await loadAppointments();
});
</script>

<template>
  <div class="flex flex-col flex-1 min-h-0 p-4">
    <div class="flex flex-wrap items-center gap-2 mb-3">
      <button
        class="inline-flex items-center justify-center w-8 h-8 rounded-md border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
        @click="shift(-1)"
      >
        <svg
          class="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            :d="ICON.chevronL[0]"
          />
        </svg>
      </button>
      <button
        class="px-3 py-1.5 text-xs rounded-lg border border-n-weak text-n-brand bg-n-background hover:bg-n-alpha-1"
        @click="today"
      >
        {{ t('ATENDIMENTOS.agenda.today') }}
      </button>
      <button
        class="inline-flex items-center justify-center w-8 h-8 rounded-md border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
        @click="shift(1)"
      >
        <svg
          class="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            :d="ICON.chevronR[0]"
          />
        </svg>
      </button>
      <span class="text-sm font-semibold capitalize text-n-slate-12">{{
        rangeLabel
      }}</span>
      <div class="flex gap-0.5 p-0.5 bg-n-alpha-1 rounded-lg dark:bg-n-solid-3">
        <button
          v-for="v in [
            ['day', 'ATENDIMENTOS.agenda.day'],
            ['week', 'ATENDIMENTOS.agenda.week'],
            ['month', 'ATENDIMENTOS.agenda.month'],
          ]"
          :key="v[0]"
          class="px-3 py-1 text-xs rounded-md text-n-slate-10"
          :class="
            viewMode === v[0] ? 'bg-n-background text-n-slate-12 shadow-sm' : ''
          "
          @click="setView(v[0])"
        >
          {{ t(v[1]) }}
        </button>
      </div>
      <select
        v-model="fPractitioner"
        class="w-full pl-3 pr-9 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 max-w-[170px]"
      >
        <option value="">
          {{ t('ATENDIMENTOS.agenda.allPractitioners') }}
        </option>
        <option v-for="p in practitioners" :key="p.id" :value="p.id">
          {{ p.name }}
        </option>
      </select>
      <select
        v-model="fService"
        class="w-full pl-3 pr-9 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 max-w-[160px]"
      >
        <option value="">{{ t('ATENDIMENTOS.agenda.allServices') }}</option>
        <option v-for="s in services" :key="s.id" :value="s.id">
          {{ s.name }}
        </option>
      </select>
      <button
        class="ml-auto inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
        @click="openNew()"
      >
        <svg
          class="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            :d="ICON.plus[0]"
          />
        </svg>
        {{ t('ATENDIMENTOS.agenda.newAppointment') }}
      </button>
    </div>

    <!-- mês -->
    <div
      v-if="viewMode === 'month'"
      class="flex flex-col flex-1 overflow-auto bg-n-solid-2 border rounded-xl border-n-weak"
    >
      <div class="grid grid-cols-7 border-b border-n-weak">
        <div
          v-for="d in ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']"
          :key="d"
          class="py-2 text-xs font-semibold text-center uppercase text-n-slate-10"
        >
          {{ t(`ATENDIMENTOS.agenda.weekdays.${d}`) }}
        </div>
      </div>
      <div class="grid flex-1 grid-cols-7">
        <div
          v-for="(cell, i) in monthGrid"
          :key="i"
          class="min-h-[96px] border-r border-b border-n-weak p-1 cursor-pointer"
          :class="cell.inMonth ? '' : 'bg-n-alpha-1 opacity-60'"
          @click="openNew(cell.date)"
        >
          <div class="text-xs text-n-slate-11">{{ cell.date.getDate() }}</div>
          <div
            v-for="a in cell.appts.slice(0, 3)"
            :key="a.id"
            class="mt-0.5 px-1 py-0.5 text-[0.625rem] rounded truncate"
            :class="STATUS[a.status]?.badge"
            @click.stop="detail = a"
          >
            {{ fmtTime(a.appointment_at) }} {{ a.patient_name }}
          </div>
          <div
            v-if="cell.appts.length > 3"
            class="text-[0.625rem] text-n-slate-10"
          >
            +{{ cell.appts.length - 3 }}
          </div>
        </div>
      </div>
    </div>

    <!-- dia / semana -->
    <div
      v-else
      class="flex flex-1 overflow-auto bg-n-solid-2 border rounded-xl border-n-weak"
    >
      <div class="shrink-0 w-14 pt-[34px]">
        <div
          v-for="h in hours"
          :key="h"
          class="relative text-[0.6875rem] text-right pr-2 text-n-slate-10"
          :style="{ height: HOUR_H + 'px' }"
        >
          <span class="absolute right-2 -top-1.5">{{ `${h}:00` }}</span>
        </div>
      </div>
      <div class="flex flex-1 min-w-0">
        <div
          v-for="(d, di) in visibleDays"
          :key="di"
          class="flex-1 border-l border-n-weak min-w-[120px]"
        >
          <div
            class="py-2 text-xs font-semibold text-center border-b border-n-weak text-n-slate-11"
            :class="{ 'bg-n-alpha-1': sameDay(d, new Date()) }"
          >
            {{ d.toLocaleDateString('pt-BR', { weekday: 'short' }) }}
            {{ d.getDate() }}
          </div>
          <div
            class="relative"
            :style="{ height: hours.length * HOUR_H + 'px' }"
            @click="openNew(d, DAY_START + Math.floor($event.offsetY / HOUR_H))"
          >
            <div
              v-for="h in hours"
              :key="h"
              class="border-b border-n-weak/60"
              :style="{ height: HOUR_H + 'px' }"
            />
            <div
              v-for="a in apptsOn(d)"
              :key="a.id"
              class="absolute left-0.5 right-0.5 rounded border-l-4 px-1 py-0.5 overflow-hidden cursor-pointer hover:shadow-md"
              :class="STATUS[a.status]?.badge"
              :style="{
                ...apptStyle(a),
                borderLeftColor: a.practitioner_color || '#6366f1',
              }"
              @click.stop="detail = a"
            >
              <div class="text-[0.625rem] font-semibold truncate">
                {{ fmtTime(a.appointment_at) }} {{ a.patient_name }}
              </div>
              <div class="text-[0.625rem] truncate opacity-80">
                {{ a.service_name }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- detalhe -->
    <div
      v-if="detail"
      class="fixed top-0 right-0 bottom-0 w-[22rem] max-w-[92vw] z-50 flex flex-col bg-n-background border-l border-n-weak shadow-xl"
    >
      <div class="flex items-center justify-between p-4 border-b border-n-weak">
        <h3 class="text-heading-3 text-n-slate-12">
          {{ t('ATENDIMENTOS.agenda.detailTitle') }}
        </h3>
        <button class="text-n-slate-10" @click="detail = null">
          <svg
            class="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div class="flex-1 p-4 space-y-3 overflow-y-auto">
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="font-medium text-n-slate-12">
            {{ detail.patient_name }}
          </div>
        </div>
        <div class="text-sm text-n-slate-11">
          <strong>{{ t('ATENDIMENTOS.form.service') }}:</strong>
          {{ detail.service_name }}
        </div>
        <div class="text-sm text-n-slate-11">
          <strong>{{ t('ATENDIMENTOS.form.practitioner') }}:</strong>
          {{ detail.practitioner_name }}
        </div>
        <div class="text-sm text-n-slate-11">
          <strong>{{ t('ATENDIMENTOS.agenda.detailWhen') }}:</strong>
          {{ new Date(detail.appointment_at).toLocaleString('pt-BR') }}
        </div>
        <div v-if="detail.price" class="text-sm text-n-slate-11">
          <strong>{{ t('ATENDIMENTOS.form.value') }}:</strong>
          {{ fmtMoney(detail.price) }}
        </div>
        <div v-if="detail.notes" class="text-sm text-n-slate-11">
          <strong>{{ t('ATENDIMENTOS.form.notes') }}:</strong>
          {{ detail.notes }}
        </div>
        <span
          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
          :class="STATUS[detail.status]?.badge"
          ><span
            class="w-1.5 h-1.5 rounded-full"
            :class="STATUS[detail.status]?.dot"
          />{{ t(STATUS[detail.status]?.labelKey) }}</span
        >
        <div class="flex flex-wrap gap-1.5 pt-2">
          <button
            v-for="st in ['confirmed', 'in_progress', 'completed']"
            :key="st"
            class="px-2.5 py-1 text-xs rounded-md border border-n-weak text-n-blue-11 bg-n-alpha-1 hover:bg-n-alpha-2"
            @click="setStatus(detail, st)"
          >
            {{ t(STATUS[st].labelKey) }}
          </button>
          <button
            class="px-2.5 py-1 text-xs rounded-md border border-n-ruby-6 text-n-ruby-11 bg-n-ruby-3 hover:bg-n-ruby-4"
            @click="setStatus(detail, 'missed')"
          >
            {{ t('ATENDIMENTOS.appointment.missed') }}
          </button>
        </div>
        <div class="flex gap-2 pt-2">
          <button
            class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
            @click="editAppt(detail)"
          >
            {{ t('ATENDIMENTOS.patients.edit') }}</button
          ><button
            class="flex-1 px-2.5 py-1 text-xs rounded-md border border-n-ruby-6 text-n-ruby-11 bg-n-ruby-3 hover:bg-n-ruby-4"
            @click="cancelAppt(detail)"
          >
            {{ t('ATENDIMENTOS.appointment.cancel') }}
          </button>
        </div>
      </div>
    </div>

    <AppointmentModal
      v-if="showModal"
      :account-id="accountId"
      :practitioners="practitioners"
      :services="services"
      :health-plans="healthPlans"
      :editing="editing"
      :prefill="prefill"
      @saved="onSaved"
      @close="showModal = false"
    />
  </div>
</template>
